import { AuthProvider, Identifier } from 'react-admin';

// Use relative URL in production (same origin), configurable for development
const API_URL = import.meta.env.VITE_API_URL || '';

export const authProvider: AuthProvider = {
    login: async ({ username, password }) => {
        const response = await fetch(`${API_URL}/api/auth/admin/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ email: username, password }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Invalid credentials' }));
            throw new Error(error.message || 'Invalid credentials');
        }

        const { expiresAt, user } = await response.json();

        // Store user info and token expiry for UI use.
        // The JWT itself is in an httpOnly cookie (not accessible to JS).
        localStorage.setItem('tokenExpiry', expiresAt);
        localStorage.setItem('user', JSON.stringify(user));

        // Clean up legacy localStorage keys from before httpOnly cookie migration
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
    },

    logout: async () => {
        try {
            await fetch(`${API_URL}/api/auth/admin/logout`, {
                method: 'POST',
                credentials: 'same-origin',
            });
        } catch {
            // Best-effort — clear local state regardless
        }
        localStorage.removeItem('tokenExpiry');
        localStorage.removeItem('user');
        // Clean up legacy keys
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
    },

    checkAuth: async () => {
        const user = localStorage.getItem('user');
        if (!user) {
            throw new Error('Not authenticated');
        }

        // Check if token is about to expire (within 5 minutes)
        const expiry = localStorage.getItem('tokenExpiry');
        if (expiry && new Date(expiry).getTime() - Date.now() < 5 * 60 * 1000) {
            await refreshAuthToken();
        }
    },

    checkError: async (error) => {
        const status = error.status;

        if (status === 401 || status === 403) {
            localStorage.removeItem('user');
            localStorage.removeItem('tokenExpiry');
            throw new Error('Session expired');
        }
    },

    getIdentity: async () => {
        const user = readStoredUser();

        // An entry with no id is not an identity react-admin can use - it would hand the
        // framework `id: undefined` and every identity-keyed lookup downstream would miss.
        // Unusable and absent mean the same thing here: route to the login page.
        if (!user || user.id === undefined || user.id === null) {
            throw new Error('Not authenticated');
        }

        return {
            id: user.id,
            fullName: user.name || user.email,
            avatar: user.avatar,
        };
    },

    getPermissions: async () => {
        return readStoredUser()?.roles ?? [];
    },
};

interface StoredUser {
    id?: Identifier;
    email?: string;
    name?: string;
    avatar?: string;
    roles?: string[];
}

/**
 * The stored user, or null if there isn't a usable one.
 *
 * Both callers used to `JSON.parse` directly, so a half-written or older-shaped `user` key threw
 * a SyntaxError out of the provider. Out of `getPermissions` that is the worse of the two: it
 * propagates through react-admin's permissions hook and takes the whole console down, when the
 * correct degradation is "no roles" — a console with nothing privileged on it. Out of
 * `getIdentity` it crashed the identity hook instead of routing to the login page.
 *
 * An unreadable entry means the same thing as an absent one: nobody is signed in.
 */
function readStoredUser(): StoredUser | null {
    const userJson = localStorage.getItem('user');
    if (!userJson) {
        return null;
    }

    try {
        const parsed = JSON.parse(userJson);
        return parsed && typeof parsed === 'object' ? (parsed as StoredUser) : null;
    } catch {
        return null;
    }
}

async function refreshAuthToken() {
    // Tokens are in httpOnly cookies — no request body needed.
    // The server reads both cookies and issues new ones.
    const response = await fetch(`${API_URL}/api/auth/admin/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
    });

    if (!response.ok) {
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiry');
        throw new Error('Session expired');
    }

    const data = await response.json();
    localStorage.setItem('tokenExpiry', data.expiresAt);
}
